import { Field, Objectclass, YesObject } from '@yes.technology/react-toolkit'
import { chunk } from 'lodash'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'
import CardsReport from 'report/CardsReport'
import ListReport from 'report/ListReport'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import useColumns from 'site/Renderer/components/DataTables/hooks/useColumns'
import useTableLayouts from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import { GroupedFieldValues } from 'site/Renderer/components/FilterFields/types'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import { RelationsTableLayouts } from '../useRelationsTableLayouts/useRelationsTableLayouts'

type UseSplitReportComponentsProps = {
  enabled: boolean
  splitThreshold: number
  fields: Field[]
  relationFields: Field[]
  filterFields?: FilterFieldsResponse
  filterComposition: string
  fieldValues: GroupedFieldValues
  yesObjects: YesObject[]
  reportType: 'list' | 'card'
  showHeaderForEveryChunk?: boolean
  layoutClassificationUuid?: string
  objectclass?: Objectclass
  relationsTableLayouts?: RelationsTableLayouts
  filteractionName?: string
  concatFields?: ConcatFields
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  isSinglePdfExport?: boolean
  isShowAllObjectFields?: boolean
}

export type SplitProgress = {
  currentChunk: number
  totalChunks: number
  isSplitting: boolean
}

export type UseSplitReportComponentsResult = {
  components: JSX.Element[]
  splitProgress: SplitProgress
}

export default function useSplitReportComponents({
  enabled,
  splitThreshold,
  fields,
  relationFields,
  filterFields,
  filterComposition,
  fieldValues,
  yesObjects,
  reportType,
  showHeaderForEveryChunk,
  layoutClassificationUuid,
  objectclass,
  relationsTableLayouts,
  filteractionName,
  concatFields,
  relationConfig,
  fieldSettings,
  isSinglePdfExport,
  isShowAllObjectFields
}: UseSplitReportComponentsProps): JSX.Element[] {
  const { t } = useTranslation()

  const {
    tableLayouts: [tableLayout]
  } = useTableLayouts({
    layoutClassificationUuid,
    objectFields: isShowAllObjectFields ? objectclass?.fields : undefined
  })

  const { allColumns: classifiedColumns } = useColumns({
    tableLayout
  })

  const title =
    objectclass?.des || filterFields?.objectclass?.des || 'Filtering Action'

  const renderListReport = useCallback(
    (objects: YesObject[], index: number) => (
      <ListReport
        key={objects[0].uuid}
        showHeader={showHeaderForEveryChunk || index === 0}
        values={fieldValues}
        title={title}
        filterComposition={filterComposition}
        filterFields={filterFields}
        objects={objects}
        columns={classifiedColumns}
        objectclass={objectclass || filterFields?.objectclass}
        filteractionName={filteractionName}
        concatFields={concatFields}
      />
    ),
    [
      classifiedColumns,
      concatFields,
      fieldValues,
      filterComposition,
      filterFields,
      filteractionName,
      objectclass,
      showHeaderForEveryChunk,
      title
    ]
  )

  const renderCardsReport = useCallback(
    (objects: YesObject[], index: number, totalObjects: number) => {
      let objectCardTitle = t('report.cards-area.title-prefix')

      const objectSid = objects[0]?.sid

      if (isSinglePdfExport) {
        objectCardTitle += `${objectSid ? ` ${objectSid}` : ''}`
      } else {
        objectCardTitle += ` ${index + 1}/${totalObjects}`
      }

      return (
        <CardsReport
          showHeader={showHeaderForEveryChunk || index === 0}
          key={objects[0].uuid}
          fields={filterFields}
          objectFields={fields}
          relationClasses={relationFields}
          values={fieldValues}
          title={title}
          cardTitle={objectCardTitle}
          objectclass={objectclass || filterFields?.objectclass}
          objects={objects}
          filterComposition={filterComposition}
          filteractionName={filteractionName}
          concatFields={concatFields}
          relationConfig={relationConfig}
          fieldSettings={fieldSettings}
          relationsTableLayouts={relationsTableLayouts}
        />
      )
    },
    [
      showHeaderForEveryChunk,
      filterFields,
      fields,
      relationFields,
      fieldValues,
      title,
      objectclass,
      filterComposition,
      filteractionName,
      concatFields,
      relationConfig,
      fieldSettings,
      relationsTableLayouts,
      isSinglePdfExport,
      t
    ]
  )

  return useMemo(() => {
    if (!enabled) return []
    return chunk(yesObjects, reportType === 'list' ? splitThreshold : 1).map(
      (objects, index) =>
        reportType === 'list'
          ? renderListReport(objects, index)
          : renderCardsReport(objects, index, yesObjects.length)
    )
  }, [
    yesObjects,
    splitThreshold,
    reportType,
    renderListReport,
    renderCardsReport,
    enabled
  ])
}
